import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  changeMyPassword as apiChangePassword,
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  type SessionUser,
  type UserRole,
} from "./authApi";

type Status = "loading" | "unauthenticated" | "authenticated";

type AuthState = {
  status: Status;
  user: SessionUser | null;
  /** true → el backend fuerza cambio de contraseña antes de dejarle usar la app. */
  mustChangePassword: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Rehidrata desde /me. Se usa manualmente si sospechamos que la sesión cambió. */
  refresh: () => Promise<void>;
  /** Comprobación de roles. Un admin siempre puede todo. */
  hasRole: (...roles: UserRole[]) => boolean;
  /** true si el usuario puede escribir (admin o contable). */
  canWrite: boolean;
  /** true si puede entrar en Cartera y vencimientos (designado por un admin). */
  hasCartera: boolean;
  /** Acceso a un apartado (admin: todos). */
  hasSection: (section: string) => boolean;
};

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [status, setStatus] = useState<Status>("loading");

  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe();
      if (me) {
        setUser(me);
        setStatus("authenticated");
      } else {
        setUser(null);
        setStatus("unauthenticated");
      }
    } catch (err) {
      // Fallo de red: dejamos al usuario en "unauthenticated" para que vea
      // la pantalla de login, es mejor que dejarlo colgado en "loading".
      console.warn("[auth] fetchMe fallo:", err);
      setUser(null);
      setStatus("unauthenticated");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    const u = await apiLogin(username, password);
    setUser(u);
    setStatus("authenticated");
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
      setStatus("unauthenticated");
    }
  }, []);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      await apiChangePassword(currentPassword, newPassword);
      // Tras cambiarla se limpia el flag; recargamos desde /me para reflejarlo.
      await refresh();
    },
    [refresh],
  );

  const hasRole = useCallback(
    (...roles: UserRole[]): boolean => {
      if (!user) return false;
      if (user.role === "admin") return true;
      return roles.includes(user.role);
    },
    [user],
  );

  const hasSection = useCallback(
    (section: string): boolean => {
      if (!user) return false;
      if (user.role === "admin") return true;
      return (user.sections ?? []).includes(section);
    },
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      mustChangePassword: !!user?.mustChangePassword,
      login,
      logout,
      changePassword,
      refresh,
      hasRole,
      canWrite: user ? user.role !== "consulta" : false,
      hasCartera: !!user && (user.role === "admin" || user.carteraAccess === true),
      hasSection,
    }),
    [status, user, login, logout, changePassword, refresh, hasRole, hasSection],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
}

/** Etiqueta amistosa para un rol. */
export function roleLabel(role: UserRole): string {
  switch (role) {
    case "admin":
      return "Administrador";
    case "contable":
      return "Contable";
    case "consulta":
      return "Consulta";
  }
}
